import { useState } from 'react';
import { FiWifiOff, FiRefreshCw } from 'react-icons/fi';
import { fireAndForget } from '../utils/fireAndForget';

interface PageLoaderProps {
  isOffline?: boolean;
  message?: string | null;
  onRetry?: () => Promise<boolean>;
}

function PageLoader({ isOffline = false, message = null, onRetry }: PageLoaderProps) {
  const [checking, setChecking] = useState(false);

  const handleRetry = async () => {
    if (onRetry) {
      setChecking(true);
      try {
        await onRetry();
      } finally {
        setChecking(false);
      }
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      className="h-screen w-screen fixed inset-0 z-[9999] bg-(--ee-surface)/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
    >
      <div className="flex flex-col items-center justify-center h-full p-4">
        {isOffline ? (
          <div className="text-center max-w-sm">
            {/* Offline Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-(--ee-tw-amber-100-bg) rounded-full flex items-center justify-center">
              <FiWifiOff className="text-3xl text-(--ee-tw-amber-600) animate-pulse" />
            </div>

            {/* Offline Message */}
            <div className="bg-(--ee-tw-gray-50-bg)/80 backdrop-blur-sm p-6 rounded-2xl border border-(--ee-tw-gray-200-line) shadow-lg">
              <h3 className="font-bold text-lg text-(--ee-tw-gray-900) mb-2">
                You&apos;re Offline
              </h3>
              <p className="text-(--ee-tw-gray-600) text-sm leading-relaxed">
                {checking
                  ? 'Checking connection...'
                  : 'Please check your internet connection and try again. Your progress is saved locally.'}
              </p>
            </div>

            {/* Retry Action */}
            <button
              className="w-full mt-6 h-12 rounded-xl bg-primary hover:bg-primary/90 text-(--ee-on-accent) font-medium transition-colors duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              onClick={fireAndForget(handleRetry)}
              disabled={checking}
            >
              <FiRefreshCw className={checking ? 'animate-spin' : ''} />
              {checking ? 'Checking...' : 'Try Again'}
            </button>
          </div>
        ) : (
          <div className="text-center">
            {/* Loading Animation */}
            <div className="mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center shadow-lg">
                <img src="/logo.webp" alt="Loading" className="w-10 h-10 animate-pulse" />
              </div>
              {/* Custom loading dots */}
              <div className="flex justify-center space-x-1">
                <div className="w-3 h-3 bg-primary rounded-full animate-bounce"></div>
                <div
                  className="w-3 h-3 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '0.1s' }}
                ></div>
                <div
                  className="w-3 h-3 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                ></div>
              </div>
            </div>

            {/* Loading Message */}
            <div className="space-y-2">
              <h3 className="font-semibold text-(--ee-tw-gray-900) text-lg">
                {message || 'Loading your content...'}
              </h3>
              <p className="text-sm text-(--ee-tw-gray-500)">Please wait a moment</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PageLoader;
